import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "../../store/RootStore";
import { getApiAsync } from "../../api";
import { restFetch } from "../../api/apiClient";
import * as workflowDefs from "../../workflows/workflowDefinitions";
import { graphqlToDate } from "../../common/utils";

/**
 * Estados que declaran `.awaits` en el DSL: qué los va a mover cuando no es ni
 * un humano ni el pipeline de la transición que los trajo (un poller, un
 * scheduler, un email). Sin esto, una edición esperando el comprobante de la
 * DNDA —que puede tardar meses— se denunciaría como estancada todos los días.
 *
 * Se leen del namespace y no por import nombrado a propósito: las constantes
 * las emite el generador y aparecen recién en la próxima regeneración, y un
 * panel de operación no puede dejar de compilar por eso. Vacío = nadie declaró
 * nada, que es el estado inicial de cualquier proyecto.
 */
const ESTADOS_QUE_ESPERAN: Record<string, Record<string, { s: string }>> = {
  ...((workflowDefs as any).SIGNED_EDITION_DNDA_WORKFLOW_AWAITING_STATES ?? {}),
  ...((workflowDefs as any).SIGNED_EDITION_SADAIC_WORKFLOW_AWAITING_STATES ?? {}),
};

export interface GroupRow {
  key: string;
  count: number;
}

/** Estados-falla de los workflows (jupiter). TODO: derivar de la definición del
 * workflow para generalizar al generador. */
const FAILURE_STATES = new Set<string>([
  "ejemplarGeneracionFallida",
  "registroDndaFallido",
  "uploadFallido",
  "subidaSadaicFallida",
  // Clase 2: el trámite se completó OK pero el avance de estado posterior falló.
  // La recovery SOLO completa el avance (no re-corre el bot).
  "registroAvanceFallido",
  "uploadAvanceFallido",
]);

/** Estados transitorios = hay un worker activo/pendiente (auto-recuperando si reintenta). */
const TRANSIENT_STATES = new Set<string>([
  "generandoEjemplar",
  "registrandoDnda",
  "subiendoEjemplar",
  "subiendoSadaic",
]);

/** failure-state → transiciones de recovery (.manualRecover) disponibles.
 * `mutation` = método del SDK (generado y gateado por permiso server-side).
 *
 * `relaunches` refleja si la transición trae `pipeline` en el DSL, o sea si
 * VUELVE A CORRER el bot o solo mueve el estado. Las dos clases se veían igual
 * en el panel, y esa confusión ya costó: se apretó "Re-registrar DNDA"
 * esperando que corriera el bot y solo cambió un estado. Si acá aparece un
 * `relaunches: true` que en el DSL no tiene pipeline (o al revés), el botón
 * vuelve a mentir — es una copia a mano de algo declarado en workflows.haiku. */
export const RECOVERY_BY_STATE: Record<
  string,
  { label: string; mutation: string; relaunches: boolean }[]
> = {
  ejemplarGeneracionFallida: [
    { label: "Reintentar generación", mutation: "signedEditionDndaWorkflow_ReintentarGeneracionEjemplar", relaunches: true },
  ],
  registroDndaFallido: [
    { label: "Reintentar registro DNDA", mutation: "signedEditionDndaWorkflow_ReintentarRegistroDnda", relaunches: true },
  ],
  uploadFallido: [
    { label: "Reintentar subida", mutation: "signedEditionDndaWorkflow_ReintentarUploadEjemplar", relaunches: true },
    { label: "Re-registrar DNDA (clave vencida)", mutation: "signedEditionDndaWorkflow_ExpirarUploadKeyDuranteUpload", relaunches: true },
  ],
  subidaSadaicFallida: [
    { label: "Reintentar subida SADAIC", mutation: "signedEditionSadaicWorkflow_ReintentarSubidaSadaic", relaunches: true },
  ],
  registroAvanceFallido: [
    { label: "Completar avance (registro OK)", mutation: "signedEditionDndaWorkflow_ReintentarAvanceRegistro", relaunches: false },
  ],
  uploadAvanceFallido: [
    { label: "Completar avance (subida OK)", mutation: "signedEditionDndaWorkflow_ReintentarAvanceUpload", relaunches: false },
  ],
};

/** Días quieta en un estado estable a partir de los cuales una edición se
 * considera estancada. No es una falla —por eso ningún tablero la mostraba— y
 * es justo la forma en que se pierde una: nadie se entera de un silencio. */
export const UMBRAL_ESTANCAMIENTO_DIAS = 3;

/** Estados donde la edición terminó su parte y no se espera movimiento. */
const ESTADOS_FINALES = new Set<string>(["comprobanteRecibido", "sadaicPublicado"]);

export type EditionHealth = "deadEnd" | "recovering" | "inProgress" | "done" | "idle";

/** Guía de resolución por failure-state: causas típicas + dónde corregir. */
export const RESOLUTION_GUIDE: Record<
  string,
  { cause: string; action: "tenant" | "payments" | "overrides" | "rerun" }[]
> = {
  registroDndaFallido: [
    { cause: "Credenciales TAD del tenant vencidas o incorrectas", action: "tenant" },
    { cause: "Falta comprobante de pago DNDA disponible", action: "payments" },
    { cause: "Dato del contrato rechazado por la DNDA (CUIT, nombre, etc.)", action: "overrides" },
  ],
  ejemplarGeneracionFallida: [
    { cause: "Partituras faltantes o archivos corruptos", action: "overrides" },
  ],
  uploadFallido: [
    { cause: "Clave de upload vencida (72hs) — re-registrar DNDA", action: "rerun" },
    { cause: "Portal DNDA caído (transitorio) — reintentar subida", action: "rerun" },
  ],
  subidaSadaicFallida: [
    { cause: "Credenciales FTP de SADAIC incorrectas (¡los errores de login no se reintentan para evitar baneos!)", action: "tenant" },
    { cause: "FTP de SADAIC inaccesible (transitorio)", action: "rerun" },
  ],
  registroAvanceFallido: [
    { cause: "El registro DNDA se completó pero el avance de estado falló (transitorio). Completar el avance — no re-registra.", action: "rerun" },
  ],
  uploadAvanceFallido: [
    { cause: "El ejemplar se subió pero el avance de estado falló (transitorio). Completar el avance — no re-sube.", action: "rerun" },
  ],
};

export interface OverrideRow {
  id: string;
  version_: string;
  path: string;
  value: unknown;
  note: string;
  active: boolean;
  createdAt_: string;
  createdBy_?: { email?: string | null } | null;
}

const byKey = (rows: GroupRow[]): Record<string, number> =>
  rows.reduce((acc, r) => ((acc[r.key] = r.count), acc), {} as Record<string, number>);

const sumFailures = (rows: GroupRow[]): number =>
  rows.filter((r) => FAILURE_STATES.has(r.key)).reduce((s, r) => s + r.count, 0);

/** Extrae el signedEditionId del payload de un WorkerEvent (varios anidamientos posibles). */
const sidFromWorkerEvent = (w: any): string | undefined => {
  const p = w?.payload ?? {};
  return (
    p.payload?.signedEditionId ??
    p.input?.signedEditionId ??
    p.input?.payload?.signedEditionId ??
    p.signedEditionId
  );
};

export class AutomationStore {
  loading = false;
  error: string | null = null;

  botRunByStatus: GroupRow[] = [];
  workerEventByStatus: GroupRow[] = [];
  botRunByType: GroupRow[] = [];
  dndaStateDist: GroupRow[] = [];
  sadaicStateDist: GroupRow[] = [];

  botRunInterventions: any[] = [];
  deadLetters: any[] = [];
  /** WorkerEvents con retryCount>0 en queued/processing (auto-recuperando). */
  recoveringEvents: any[] = [];
  /** signedEditionId → nombre (data_.name), para mostrar en los items. */
  editionNames: Record<string, string> = {};
  /** Tablero: TODAS las SignedEditions con sus estados. */
  allEditions: any[] = [];
  pendingEditions: any[] = [];
  /** Ediciones PRE-firma cuyo envío a firma falló (documentationState =
   *  signatureSendingFailed): invisibles al panel post-firma (SignedEdition). */
  signatureFailedEditions: any[] = [];
  recheckingEdition: string | null = null;
  resolving: string | null = null;

  /** WorkerEvent objetivo del modal "Destrabar" (retry con/sin modificación). */
  retryTarget: any | null = null;

  /** Pestaña activa (0 intervención, 1 recuperando, 2 en proceso, 3 analítica). */
  tab = 0;
  /** Correcciones de trámite por edición (SnapshotOverride). */
  overridesByEdition: Record<string, OverrideRow[]> = {};
  /** Histórico para analítica (WorkerEvents del período). */
  historyEvents: any[] = [];
  historySince: Date = new Date(Date.now() - 24 * 3600 * 1000);
  historyLoading = false;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false } as any);
  }

  setTab(t: number) {
    this.tab = t;
  }

  private async api() {
    return getApiAsync(this.rootStore.authStore as any, (this.rootStore as any).activityStore);
  }

  private async widget(key: string): Promise<GroupRow[]> {
    try {
      const res = await restFetch<{ data: { groupKey: string | null; count: number }[] }>(
        `/api/dashboard/widgets/${key}/data?`
      );
      return (res?.data ?? []).map((r) => ({ key: r.groupKey ?? "—", count: r.count }));
    } catch {
      return [];
    }
  }

  async fetchAll() {
    this.loading = true;
    this.error = null;
    try {
      const [brs, wes, brt, dnda, sadaic] = await Promise.all([
        this.widget("botRun-by-status"),
        this.widget("workerEvent-by-status"),
        this.widget("botRun-by-botType"),
        this.widget("signedEdition-by-dndaState"),
        this.widget("signedEdition-by-sadaicState"),
      ]);
      const api = await this.api();
      const [biRes, dlRes, recRes, edRes] = await Promise.all([
        api.automationBotRuns({
          where: { status: { equals: "requires_intervention" } } as any,
          offset: 0,
          take: 50,
        }),
        api.simWorkerEvents({
          where: { status: { equals: "dead_letter" } } as any,
          orderBy: [{ enqueuedAt: "desc" }] as any,
          offset: 0,
          take: 50,
        } as any),
        api.simWorkerEvents({
          where: { retryCount: { gt: 0 }, status: { in: ["queued", "processing"] } } as any,
          orderBy: [{ enqueuedAt: "desc" }] as any,
          offset: 0,
          take: 50,
        } as any),
        api.automationEditions({ offset: 0, take: 200 }),
      ]);
      // Resiliente: si el usuario no tiene el permiso del recheck, este query tira;
      // no debe romper el resto del dashboard.
      let pendingEditions: any[] = [];
      try {
        const pendingRes = await api.editionsPendingSignatureCompletion();
        pendingEditions = ((pendingRes as any).editionsPendingSignatureCompletion ?? []) as any[];
      } catch {
        /* sin permiso / error → lista vacía */
      }
      // Ediciones pre-firma cuyo envío a firma falló. Query genérica (tenant-scoped,
      // igual que editionsPendingSignatureCompletion, su sibling pre-firma). El campo
      // documentationLastOperationalError trae el "por qué" persistido server-side.
      let signatureFailed: any[] = [];
      try {
        const sfRes = await (api as any).editionListWithOffset({
          where: { documentationState: { id: { equals: "signatureSendingFailed" } } },
          offset: 0,
          take: 100,
        });
        signatureFailed = ((sfRes as any).editionListWithOffset ?? []) as any[];
      } catch {
        /* sin permiso / error → lista vacía */
      }
      runInAction(() => {
        this.botRunByStatus = brs;
        this.workerEventByStatus = wes;
        this.botRunByType = brt;
        this.dndaStateDist = dnda;
        this.sadaicStateDist = sadaic;
        this.botRunInterventions = ((biRes as any).botRunListWithOffset ?? []) as any[];
        this.deadLetters = ((dlRes as any).workerEventListWithOffset ?? []) as any[];
        this.recoveringEvents = ((recRes as any).workerEventListWithOffset ?? []) as any[];
        this.allEditions = ((edRes as any).signedEditionListWithOffset ?? []) as any[];
        this.pendingEditions = pendingEditions;
        this.signatureFailedEditions = signatureFailed;
      });

      // Nombres de edición (data_.name) para los items que referencian SignedEdition.
      const sids = new Set<string>();
      for (const r of ((biRes as any).botRunListWithOffset ?? []) as any[]) {
        if (r.relatedEntityType === "SignedEdition" && r.relatedEntityId) sids.add(r.relatedEntityId);
      }
      for (const w of ((dlRes as any).workerEventListWithOffset ?? []) as any[]) {
        const sid = sidFromWorkerEvent(w);
        if (sid) sids.add(sid);
      }
      if (sids.size > 0) {
        const ids = Array.from(sids);
        const nres = await api.simEditions({ where: { id: { in: ids } } as any, offset: 0, take: ids.length });
        runInAction(() => {
          const names: Record<string, string> = {};
          for (const e of ((nres as any).signedEditionListWithOffset ?? []) as any[]) {
            if (e?.id && e?.data_?.name) names[e.id] = e.data_.name;
          }
          this.editionNames = names;
        });
      }
    } catch (e: any) {
      runInAction(() => (this.error = e?.message ?? "Error cargando el dashboard"));
    } finally {
      runInAction(() => (this.loading = false));
    }
  }

  /** Buckets de salud. `intervention` usa el largo de las listas (exacto hasta 50). */
  get buckets() {
    const br = byKey(this.botRunByStatus);
    const we = byKey(this.workerEventByStatus);
    return {
      intervention:
        this.botRunInterventions.length +
        this.deadLetters.length +
        sumFailures(this.dndaStateDist) +
        sumFailures(this.sadaicStateDist),
      recovering: this.recoveringEvents.length,
      inProgress:
        (br["running"] ?? 0) + (br["pending"] ?? 0) + (we["queued"] ?? 0) + (we["processing"] ?? 0),
      completed: (br["completed"] ?? 0) + (we["completed"] ?? 0),
      failed: (br["failed"] ?? 0) + (we["failed"] ?? 0),
    };
  }

  isFailureState(state: string): boolean {
    return FAILURE_STATES.has(state);
  }

  /** Salud de una edición del tablero: dead-end > recuperando > en curso > done > idle. */
  healthFor(ed: any): EditionHealth {
    const dnda = ed?.dndaState?.id as string | undefined;
    const sadaic = ed?.sadaicState?.id as string | undefined;
    if ((dnda && FAILURE_STATES.has(dnda)) || (sadaic && FAILURE_STATES.has(sadaic))) return "deadEnd";
    const transient = (dnda && TRANSIENT_STATES.has(dnda)) || (sadaic && TRANSIENT_STATES.has(sadaic));
    if (transient) {
      const retrying = this.recoveringEvents.some((w: any) => sidFromWorkerEvent(w) === ed.id);
      return retrying ? "recovering" : "inProgress";
    }
    if (dnda === "comprobanteRecibido" && sadaic === "sadaicPublicado") return "done";
    return "idle";
  }

  /** Info del reintento en curso para una edición auto-recuperando: intento k + próximo. */
  retryInfoFor(ed: any): { attempt: number; nextRetryAt: string | null } | null {
    const w = this.recoveringEvents.find((x: any) => sidFromWorkerEvent(x) === ed.id);
    return w ? { attempt: w.retryCount ?? 0, nextRetryAt: w.nextRetryAt ?? null } : null;
  }

  /** Opciones de recovery (.manualRecover) para una edición en dead-end. */
  recoveryOptionsFor(ed: any): { label: string; mutation: string; relaunches: boolean; workflow: "dnda" | "sadaic" }[] {
    const out: { label: string; mutation: string; relaunches: boolean; workflow: "dnda" | "sadaic" }[] = [];
    const dnda = ed?.dndaState?.id as string | undefined;
    const sadaic = ed?.sadaicState?.id as string | undefined;
    if (dnda && RECOVERY_BY_STATE[dnda]) out.push(...RECOVERY_BY_STATE[dnda].map((o) => ({ ...o, workflow: "dnda" as const })));
    if (sadaic && RECOVERY_BY_STATE[sadaic]) out.push(...RECOVERY_BY_STATE[sadaic].map((o) => ({ ...o, workflow: "sadaic" as const })));
    return out;
  }

  /** Re-verifica las firmas de una edición atascada en sentForSignature; si están
   *  todas, el server la congela en SignedEdition. Refresca la lista al terminar. */
  async recheckEdition(editionId: string) {
    this.error = null;
    this.recheckingEdition = editionId;
    try {
      const api = await this.api();
      await api.recheckEditionSignatures({ editionId });
      await this.fetchAll();
    } catch (e: any) {
      runInAction(() => (this.error = e?.message ?? "Error al re-verificar firmas"));
    } finally {
      runInAction(() => (this.recheckingEdition = null));
    }
  }

  /** Dispara la transición de recovery (mutation .manualRecover gateada server-side). */
  async resolveEdition(ed: any, mutation: string) {
    this.error = null;
    this.resolving = ed.id;
    try {
      const api = await this.api();
      await (api as any)[mutation]({
        instanceId: ed.id,
        instanceVersion: ed.version_,
        instanceEntity: "SignedEdition",
      });
      await this.fetchAll();
    } catch (e: any) {
      runInAction(() => (this.error = e?.message ?? "Error al resolver"));
    } finally {
      runInAction(() => (this.resolving = null));
    }
  }

  /** Reintenta el envío a firma de una edición pre-firma en signatureSendingFailed
   *  (transición retrySendForSignature → sendingForSignature). El server limpia
   *  documentationLastOperationalError al reintentar. */
  async retrySendForSignature(ed: any) {
    this.error = null;
    this.resolving = ed.id;
    try {
      const api = await this.api();
      await (api as any).editionDocumentationWorkflow_RetrySendForSignature({
        instanceId: ed.id,
        instanceVersion: ed.version_,
        instanceEntity: "Edition",
      });
      await this.fetchAll();
    } catch (e: any) {
      runInAction(() => (this.error = e?.message ?? "Error al reintentar el envío a firma"));
    } finally {
      runInAction(() => (this.resolving = null));
    }
  }

  /** Nombre legible (data_.name) de la entidad referenciada, si es SignedEdition. */
  nameForBotRun(r: any): string | null {
    return r?.relatedEntityType === "SignedEdition" && r?.relatedEntityId
      ? this.editionNames[r.relatedEntityId] ?? null
      : null;
  }

  nameForWorkerEvent(w: any): string | null {
    const sid = sidFromWorkerEvent(w);
    return sid ? this.editionNames[sid] ?? null : null;
  }

  openRetry(workerEvent: any) {
    this.retryTarget = workerEvent;
  }

  closeRetry() {
    this.retryTarget = null;
  }

  async retryInfo(workerEventId: string) {
    const api = await this.api();
    const res = await api.workerEventRetryInfo({ id: workerEventId });
    return (res as any).workerEventRetryInfo;
  }

  async doRetry(workerEventId: string, mode: string, overrides?: Record<string, unknown>) {
    this.error = null;
    try {
      const api = await this.api();
      await api.workerEventRetry({ id: workerEventId, mode, overrides } as any);
      runInAction(() => (this.retryTarget = null));
      await this.fetchAll();
    } catch (e: any) {
      runInAction(() => (this.error = e?.message ?? "Error al reintentar"));
    }
  }

  // ── Correcciones de trámite (SnapshotOverride) ────────────────────────────

  async loadOverrides(editionId: string) {
    try {
      const api = await this.api();
      const res = await (api as any).automationOverrides({
        where: {
          relatedEntityType: { equals: "SignedEdition" },
          relatedEntityId: { equals: editionId },
        },
        offset: 0,
        take: 100,
      });
      runInAction(() => {
        this.overridesByEdition[editionId] = (res.snapshotOverrideListWithOffset ?? []) as OverrideRow[];
      });
    } catch (e: any) {
      runInAction(() => (this.error = e?.message ?? "Error al cargar correcciones"));
    }
  }

  async createOverride(editionId: string, path: string, value: unknown, note: string) {
    this.error = null;
    try {
      const api = await this.api();
      await (api as any).automationOverrideCreate({
        relatedEntityType: "SignedEdition",
        relatedEntityId: editionId,
        path,
        value,
        note,
        active: true,
      });
      await this.loadOverrides(editionId);
    } catch (e: any) {
      runInAction(() => (this.error = e?.message ?? "Error al crear la corrección"));
      throw e;
    }
  }

  async setOverrideActive(editionId: string, o: OverrideRow, active: boolean) {
    this.error = null;
    try {
      const api = await this.api();
      await (api as any).automationOverrideSetActive({ id: o.id, version_: o.version_, active });
      await this.loadOverrides(editionId);
    } catch (e: any) {
      runInAction(() => (this.error = e?.message ?? "Error al actualizar la corrección"));
    }
  }

  /** WorkerEvent dead-letter asociado a una edición (diagnóstico de la card). */
  deadLetterForEdition(editionId: string): any | undefined {
    return this.deadLetters.find((w: any) => sidFromWorkerEvent(w) === editionId);
  }

  /** BotRun en intervención asociado a una edición. */
  botRunForEdition(editionId: string): any | undefined {
    return this.botRunInterventions.find((r: any) => r.relatedEntityId === editionId);
  }

  /** Ediciones en dead-end (las cards de la pestaña Intervención). */
  get interventionEditions(): any[] {
    return this.allEditions.filter((ed) => this.healthFor(ed) === "deadEnd");
  }

  // ── Problemas de bot: uno por (entidad, bot), no uno por intento ──────────
  //
  // `BotRun.status = requires_intervention` no se limpia nunca: es un registro
  // forense de un intento, no el estado de un problema. Listarlos crudos daba
  // 39 renglones, con la misma edición repetida cuatro veces por el mismo error
  // y corridas de hace dos semanas sobre ediciones que ya siguieron su camino.
  // Un tablero así no se lee, y lo que no se lee no se mira.

  /** Texto libre sobre el nombre de la edición. */
  filtroTexto = "";
  /** botType exacto, "" = todos. */
  filtroBot = "";
  /** Por defecto se esconde lo histórico: es lo que hacía ilegible la lista. */
  soloVigentes = true;

  setFiltroTexto(v: string) { this.filtroTexto = v; }
  setFiltroBot(v: string) { this.filtroBot = v; }
  setSoloVigentes(v: boolean) { this.soloVigentes = v; }

  /** Los botType presentes, para poblar el filtro sin hardcodearlos. */
  get botTypesEnProblemas(): string[] {
    return Array.from(new Set(this.botRunInterventions.map((r: any) => r.botType).filter(Boolean))).sort();
  }

  /**
   * Un intento pide intervención mientras su entidad SIGA con un problema.
   *
   * Si la edición ya no está en un estado de falla, o bien avanzó o bien hay un
   * worker corriendo: en los dos casos no hay nada que decidir, y el BotRun es
   * historia. Si la edición no está entre las cargadas, no se puede afirmar que
   * se resolvió — se conserva como vigente antes que esconderla.
   */
  private problemaVigente(run: any): boolean {
    if (run?.relatedEntityType !== "SignedEdition") return true;
    const ed = this.allEditions.find((e: any) => e.id === run.relatedEntityId);
    if (!ed) return true;
    return this.healthFor(ed) === "deadEnd";
  }

  /** Un renglón por (entidad, bot): el intento más reciente y cuántos hubo. */
  get botProblems(): {
    key: string;
    run: any;
    intentos: number;
    primero: string | null;
    vigente: boolean;
  }[] {
    const grupos = new Map<string, any[]>();
    for (const r of this.botRunInterventions) {
      const key = `${r.relatedEntityType ?? "?"}:${r.relatedEntityId ?? "?"}:${r.botType ?? "?"}`;
      (grupos.get(key) ?? grupos.set(key, []).get(key)!).push(r);
    }
    const desc = (a: any, b: any) =>
      new Date(b.startedAt ?? 0).getTime() - new Date(a.startedAt ?? 0).getTime();

    return Array.from(grupos.entries())
      .map(([key, runs]) => {
        const ordenados = [...runs].sort(desc);
        return {
          key,
          run: ordenados[0],
          intentos: ordenados.length,
          primero: ordenados[ordenados.length - 1]?.startedAt ?? null,
          vigente: this.problemaVigente(ordenados[0]),
        };
      })
      .sort((a, b) => desc(a.run, b.run));
  }

  /** Los de arriba, pasados por los filtros de la barra. */
  get botProblemsFiltrados() {
    const texto = this.filtroTexto.trim().toLowerCase();
    return this.botProblems.filter((p) => {
      if (this.soloVigentes && !p.vigente) return false;
      if (this.filtroBot && p.run.botType !== this.filtroBot) return false;
      if (texto) {
        const nombre = (this.nameForBotRun(p.run) ?? p.run.relatedEntityId ?? "").toLowerCase();
        if (!nombre.includes(texto)) return false;
      }
      return true;
    });
  }

  /** Cuántos quedaron afuera por ser historia (se ofrece mostrarlos). */
  get botProblemsHistoricos(): number {
    return this.botProblems.filter((p) => !p.vigente).length;
  }

  /**
   * Qué está esperando esta edición, si su estado lo declaró en el DSL.
   * `null` = nadie declaró nada, y entonces quieta sí es sospechoso.
   */
  awaitingTextFor(ed: any): string | null {
    const idioma = (this.rootStore as any)?.langStore?.language ?? "es";
    for (const estado of [ed?.dndaState?.id, ed?.sadaicState?.id]) {
      const etiquetas = estado ? ESTADOS_QUE_ESPERAN[estado] : undefined;
      if (etiquetas) return etiquetas[idioma]?.s ?? etiquetas.es?.s ?? null;
    }
    return null;
  }

  // ── Estancadas: el silencio, que no es una falla y por eso no se veía ──────

  /**
   * Ediciones quietas en un estado ESTABLE (ni falla ni transitorio ni final)
   * hace más de UMBRAL_ESTANCAMIENTO_DIAS.
   *
   * Es la forma exacta en que se perdió "260726 x2 Audios": seis días en
   * `ejemplarGenerado`, sin error, sin worker, sin figurar en ningún lado —
   * porque el panel solo miraba estados de falla. `lastUpdatedAt_` no es la
   * fecha del cambio de estado, pero toda transición lo toca, así que como cota
   * inferior del silencio alcanza.
   */
  get stalledEditions(): { ed: any; dias: number; estado: string }[] {
    const corte = UMBRAL_ESTANCAMIENTO_DIAS * 24 * 3600 * 1000;
    const out: { ed: any; dias: number; estado: string }[] = [];
    for (const ed of this.allEditions) {
      if (this.healthFor(ed) !== "idle") continue;
      const dnda = ed?.dndaState?.id as string | undefined;
      const sadaic = ed?.sadaicState?.id as string | undefined;
      // Terminado de los dos lados: no se espera movimiento.
      if (dnda && sadaic && ESTADOS_FINALES.has(dnda) && ESTADOS_FINALES.has(sadaic)) continue;
      // Declaró que espera algo de afuera: quieta no es lo mismo que trabada.
      if (this.awaitingTextFor(ed)) continue;
      const ts = ed?.lastUpdatedAt_ ? new Date(ed.lastUpdatedAt_).getTime() : NaN;
      if (!Number.isFinite(ts)) continue;
      const quieta = Date.now() - ts;
      if (quieta < corte) continue;
      out.push({
        ed,
        dias: Math.floor(quieta / (24 * 3600 * 1000)),
        estado: (dnda && !ESTADOS_FINALES.has(dnda) ? dnda : sadaic) ?? "—",
      });
    }
    return out.sort((a, b) => b.dias - a.dias);
  }

  /** Total de ítems que requieren intervención humana (badge + card de Tab 0).
   *  Cuenta problemas, no intentos: antes sumaba los BotRun crudos y el badge
   *  decía 39 cuando había un puñado de cosas para decidir. */
  get interventionCount(): number {
    const yaEnCards = new Set(this.interventionEditions.map((ed: any) => ed.id));
    const sueltos = this.botProblems.filter(
      (p) => p.vigente && !yaEnCards.has(p.run.relatedEntityId)
    ).length;
    return (
      this.interventionEditions.length + sueltos + this.signatureFailedEditions.length
    );
  }

  /** Ediciones auto-recuperando (pestaña 2). */
  get recoveringEditions(): any[] {
    return this.allEditions.filter((ed) => this.healthFor(ed) === "recovering");
  }

  /** Ediciones en proceso sin incidente (pestaña 3). */
  get inProgressEditions(): any[] {
    return this.allEditions.filter((ed) => this.healthFor(ed) === "inProgress");
  }

  // ── Histórico (analítica client-side) ─────────────────────────────────────

  setHistorySince(d: Date) {
    this.historySince = d;
    void this.loadHistory();
  }

  async loadHistory() {
    this.historyLoading = true;
    try {
      const api = await this.api();
      // Paginado: MAX_PAGE_SIZE del server es 501 (y skip máx 2001) → páginas
      // de 500 hasta agotar el período o el tope de 2000 registros.
      const PAGE = 500;
      const MAX_TOTAL = 2000;
      const all: any[] = [];
      for (let offset = 0; offset < MAX_TOTAL; offset += PAGE) {
        const res = await (api as any).automationHistory({
          since: this.historySince.toISOString(),
          offset,
          take: PAGE,
        });
        const page = (res.workerEventListWithOffset ?? []) as any[];
        all.push(...page);
        if (page.length < PAGE) break;
      }
      runInAction(() => {
        this.historyEvents = all;
      });
    } catch (e: any) {
      runInAction(() => (this.error = e?.message ?? "Error al cargar el histórico"));
    } finally {
      runInAction(() => (this.historyLoading = false));
    }
  }

  /** Agregación por día × estado para las barras del histórico. */
  get historyByDay(): { day: string; completed: number; failed: number; dead_letter: number; otros: number }[] {
    const days: Record<string, { completed: number; failed: number; dead_letter: number; otros: number }> = {};
    for (const w of this.historyEvents) {
      // DD/MM/AAAA con locale explícito: sin él, un navegador en inglés
      // rotulaba el 1 de octubre "10/1/2026" (KAN-443).
      const day = graphqlToDate(w.enqueuedAt);
      const d = (days[day] ??= { completed: 0, failed: 0, dead_letter: 0, otros: 0 });
      if (w.status === "completed") d.completed++;
      else if (w.status === "failed") d.failed++;
      else if (w.status === "dead_letter") d.dead_letter++;
      else d.otros++;
    }
    return Object.entries(days)
      .map(([day, v]) => ({ day, ...v }))
      .reverse();
  }

  /** Agregación por pipeline para el histórico. */
  get historyByPipeline(): GroupRow[] {
    const acc: Record<string, number> = {};
    for (const w of this.historyEvents) {
      const key = String(w.jobType ?? "").split(".")[0] || "—";
      acc[key] = (acc[key] ?? 0) + 1;
    }
    return Object.entries(acc).map(([key, count]) => ({ key, count }));
  }
}
